import React from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Compass, Rocket, Users } from 'lucide-react';

function AboutUs() {
  return (
    <div style={{
      backgroundColor: '#0a0e1a',
      minHeight: '100vh',
      padding: '60px'
    }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '60px', textAlign: 'center' }}>
          <h1 style={{
            fontSize: '48px',
            fontWeight: '800',
            margin: '0 0 20px 0',
            color: '#ffffff',
            letterSpacing: '-0.5px'
          }}>
            About Upperline Bet
          </h1>
          <p style={{
            fontSize: '18px',
            color: '#64748b',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.7'
          }}>
            Professional sports betting analytics for data-driven bettors
          </p>
        </div>

        {/* A Note from the Founder */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '12px',
          padding: '40px',
          marginBottom: '40px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            marginBottom: '25px',
            paddingLeft: '20px',
            borderLeft: '4px solid #00d9ff'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              color: '#ffffff'
            }}>
              A Note from the Founder
            </h2>
          </div>

          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#a0aec0',
            marginBottom: '20px'
          }}>
            I first learned about arbitrage betting while studying Aerospace Engineering at MIT. The math was
            straightforward. When sportsbooks disagree on the odds, you can bet both sides and lock in a profit
            regardless of the outcome. But back then, sports betting was only legal in a few states, so the idea
            stayed theoretical.
          </p>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#a0aec0',
            marginBottom: '20px'
          }}>
            That changed when legalization started spreading across the country. Suddenly there were dozens of
            sportsbooks competing for customers, throwing out promotional offers and occasionally posting odds
            that didn't quite match up. I started hunting for these opportunities by hand: spreadsheets, too many
            browser tabs, manually comparing lines across sites. It worked, but it was painfully slow.
          </p>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#a0aec0',
            marginBottom: '20px'
          }}>
            So I wrote some Python scripts to automate the scanning. Nothing fancy at first, just doing what I
            was already doing manually. Over time, those scripts grew more sophisticated, and I started tracking
            positive EV opportunities and bonus conversions alongside the arbitrage plays.
          </p>

          {/* Pull Quote / Stat Callout */}
          <div style={{
            margin: '30px 0',
            padding: '25px 30px',
            backgroundColor: '#0f1419',
            borderRadius: '10px',
            borderLeft: '4px solid #00d9ff',
            display: 'flex',
            alignItems: 'center',
            gap: '30px'
          }}>
            <div style={{
              fontSize: '42px',
              fontWeight: '800',
              color: '#00d9ff',
              fontFamily: 'monospace',
              whiteSpace: 'nowrap'
            }}>
              $10K+
            </div>
            <div style={{
              fontSize: '15px',
              lineHeight: '1.6',
              color: '#a0aec0'
            }}>
              Annual profit in my first two years, while keeping a full-time job. The volume of betting
              earned me VIP status with DraftKings which included my own host and invites to private events.
            </div>
          </div>

          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#a0aec0',
            marginBottom: '20px'
          }}>
            When I started talking about this with friends and family, the response was always the same:
            "Can you bet with my money for me?" or "How do you know what bets to place?" The 
            problem was, sharing my scrappy Python scripts wasn't exactly user-friendly. So I built Upperline Bet. 
            
          </p>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#a0aec0',
            marginBottom: '30px'
          }}>
            I work in software 
            at a robotics startup, and I've always been good at breaking down complex ideas into something that
            makes sense. That's what sets Upperline Bet apart: making these strategies accessible to anyone
            willing to learn. I still use these tools myself. My goal isn't just to hand
            people a tool. It's to teach them how to think like an advantage bettor. Beyond the money, there's
            something satisfying about using math to gain an edge. 
          </p>

          {/* Signature Block */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            paddingTop: '20px',
            borderTop: '1px solid #2d3748'
          }}>
            {/* Headshot Placeholder */}
            <div style={{
              width: '90px',
              height: '90px',
              borderRadius: '50%',
              border: '2px solid #00d9ff',
              flexShrink: 0,
              overflow: 'hidden'
            }}>
              <img
                src="/founder.JPG"
                alt="Charlie, Founder of Upperline Bet"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '4px'
              }}>
                Charlie
              </div>
              <div style={{
                fontSize: '14px',
                color: '#64748b'
              }}>
                Founder, Upperline Bet
              </div>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '20px',
          marginBottom: '40px'
        }}>
          <div style={{
            backgroundColor: '#1a1f2e',
            border: '1px solid #2d3748',
            borderRadius: '12px',
            padding: '25px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '800',
              color: '#00d9ff',
              marginBottom: '8px',
              fontFamily: 'monospace'
            }}>
              18
            </div>
            <div style={{
              fontSize: '14px',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Sportsbooks
            </div>
          </div>
          <div style={{
            backgroundColor: '#1a1f2e',
            border: '1px solid #2d3748',
            borderRadius: '12px',
            padding: '25px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '800',
              color: '#10b981',
              marginBottom: '8px',
              fontFamily: 'monospace'
            }}>
              3 min
            </div>
            <div style={{
              fontSize: '14px',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Update Frequency
            </div>
          </div>
          <div style={{
            backgroundColor: '#1a1f2e',
            border: '1px solid #2d3748',
            borderRadius: '12px',
            padding: '25px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '800',
              color: '#8b5cf6',
              marginBottom: '8px',
              fontFamily: 'monospace'
            }}>
              2+ yrs
            </div>
            <div style={{
              fontSize: '14px',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Proven Results
            </div>
          </div>
        </div>

        {/* What We Do */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '12px',
          padding: '40px',
          marginBottom: '40px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            marginBottom: '25px',
            paddingLeft: '20px',
            borderLeft: '4px solid #10b981'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(0, 217, 255, 0.2) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <BarChart3 size={24} color="#10b981" />
            </div>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              color: '#ffffff'
            }}>
              What We Do
            </h2>
          </div>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#a0aec0',
            marginBottom: '20px'
          }}>
            Our platform monitors odds across 18 sportsbooks, updating every 3 minutes to catch pricing
            discrepancies as they happen. When books disagree on the odds, or when the market has mispriced
            an outcome, our system flags it.
          </p>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#a0aec0',
            marginBottom: '20px'
          }}>
            We currently focus on three core strategies. Arbitrage lets you lock in profit regardless of outcome by
            exploiting price differences between sportsbooks. Positive EV betting identifies lines where the
            true probability gives you a mathematical edge over the long run. And bonus conversion turns the
            promotional offers that sportsbooks use as customer acquisition into actual cash through optimal hedging.
          </p>

          {/* Pull Quote */}
          <div style={{
            margin: '25px 0',
            padding: '20px 25px',
            backgroundColor: '#0f1419',
            borderRadius: '10px',
            borderLeft: '4px solid #10b981'
          }}>
            <p style={{
              fontSize: '17px',
              fontStyle: 'italic',
              lineHeight: '1.7',
              color: '#ffffff',
              margin: 0
            }}>
              "No black boxes, no 'trust me' picks, just data."
            </p>
          </div>

          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#a0aec0',
            margin: 0
          }}>
            Every calculation on the platform is transparent. We show you the math, the expected value, and
            recommended bet sizes using the Kelly Criterion, an algorithm that determines optimal bet sizing
            based on your edge and bankroll.
          </p>
        </div>

        {/* What to Expect */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '12px',
          padding: '40px',
          marginBottom: '40px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            marginBottom: '25px',
            paddingLeft: '20px',
            borderLeft: '4px solid #6366f1'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Compass size={24} color="#6366f1" />
            </div>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              color: '#ffffff'
            }}>
              What to Expect
            </h2>
          </div>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#a0aec0',
            marginBottom: '20px'
          }}>
            There's a learning curve at the start: understanding which sportsbooks limit winners and how,
            learning to move quickly when odds shift, and managing the risk of prices changing while you're
            switching between apps to place your bets.
          </p>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#a0aec0',
            margin: 0
          }}>
            In just over two years, I've had three significant losses from odds moving before I could get
            both sides down. Each time, I made the money back within a week or so. That's the reality of
            this: individual bets can go wrong, but the math works over time. If you're patient and
            disciplined, the edge compounds.
          </p>
        </div>

        {/* Coming Soon */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '12px',
          padding: '40px',
          marginBottom: '40px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            marginBottom: '25px',
            paddingLeft: '20px',
            borderLeft: '4px solid #8b5cf6'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Rocket size={24} color="#8b5cf6" />
            </div>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              color: '#ffffff'
            }}>
              Coming Soon
            </h2>
          </div>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#a0aec0',
            marginBottom: '20px'
          }}>
            This website is just the beginning. I'm actively building new features and want to hear from
            people who use the site. This is for the users. If there's something you want added, {' '}
            <Link
              to="/contact"
              style={{
                color: '#8b5cf6',
                textDecoration: 'none',
                fontWeight: '500'
              }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              let me know
            </Link>.
          </p>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#a0aec0',
            marginBottom: '15px'
          }}>
            Here's what's on the roadmap:
          </p>
          <ul style={{
            margin: 0,
            paddingLeft: '20px',
            color: '#a0aec0',
            fontSize: '16px',
            lineHeight: '2.2'
          }}>
            <li><strong style={{ color: '#ffffff' }}>Player props</strong> — first priority, coming soon</li>
            <li><strong style={{ color: '#ffffff' }}>More sportsbooks</strong> — expanding beyond the current 18, including futures markets</li>
            <li><strong style={{ color: '#ffffff' }}>Faster updates</strong> — working toward more frequent odds refreshes</li>
            <li><strong style={{ color: '#ffffff' }}>Strategy tracking</strong> — see how specific positive EV strategies have performed over time</li>
            <li><strong style={{ color: '#ffffff' }}>More promo tools</strong> — profit boosts, no-sweat bets, and other sportsbook promotions</li>
            <li><strong style={{ color: '#ffffff' }}>Educational content</strong> — articles on how to use these tools effectively</li>
            <li><strong style={{ color: '#ffffff' }}>Live odds</strong> — longer-term goal</li>
          </ul>
        </div>

        {/* Who We're For */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '12px',
          padding: '40px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            marginBottom: '25px',
            paddingLeft: '20px',
            borderLeft: '4px solid #f59e0b'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(239, 68, 68, 0.2) 100%)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Users size={24} color="#f59e0b" />
            </div>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              color: '#ffffff'
            }}>
              Who We're For
            </h2>
          </div>

          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#a0aec0',
            marginBottom: '20px'
          }}>
            This doesn't require hours of research or quitting your day job. Check in a few times a day,
            place a few bets, and let the math work in your favor. That's it. A few minutes of effort
            can translate into real, consistent returns over time.
          </p>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#a0aec0',
            marginBottom: '20px'
          }}>
            You don't need a finance background or years of betting experience. You need patience,
            discipline, and the willingness to trust the math. Our users are people
            who want a smarter approach to sports betting and some extra money in their pocket.
          </p>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#a0aec0',
            margin: 0
          }}>
            If you're ready to approach betting with an edge, Upperline Bet gives you the tools to do it right.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AboutUs;
